import { useEffect, useRef, useState } from 'react';

import { useQueryClient } from 'react-query';
import { useParams } from 'react-router-dom';

import { TrackingEventsV2 } from '@/analytics/eventsV2';
import { trackCustomEvent } from '@/analytics/Mixpanel';
import useUpdateFact from '@/api/mutations/useUpdateFact';
import { useGetFactDetails } from '@/api/queries/useGetFactDetails';
import { useGetSubset } from '@/api/queries/useGetSubset';
import { QueryEntity } from '@/api/types';
import { capitalizeFirstLetter } from '@/helpers/filterNames';
import { Chip } from '@/new/components/chip';
import { CommentSection } from '@/new/components/fact/comments-section';
import { FactDocumentDetails } from '@/new/components/fact/document-details';
import { RelevanceSelector } from '@/new/components/fact/relevance-selector';
import { Icon } from '@/new/components/icon';
import { ProcessingChip, ProcessingState } from '@/new/components/processing-chip';
import { Button } from '@/new/components/shadcn/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/new/components/shadcn/dialog';
import { Input } from '@/new/components/shadcn/input';
import { Label } from '@/new/components/shadcn/label';
import { Popover, PopoverContent, PopoverTrigger } from '@/new/components/shadcn/popover';
import { Skeleton } from '@/new/components/shadcn/skeleton';
import { Textarea } from '@/new/components/shadcn/textarea';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/new/components/shadcn/tooltip';
import { TooltipIcon } from '@/new/components/tooltip-icon';
import { CHIP_COLOURS } from '@/new/lib/chip-colours';
import { cn } from '@/new/lib/utils';
import { Relevance } from '@/types';

interface EditFactFieldDialogProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  label: string;
  value: string;
  onValueChange: (value: string) => void;
  onSubmit: (value: string) => void;
  originalValue: string;
  buttonClassName?: string;
}

const EditFactFieldDialog = ({
  isOpen,
  onOpenChange,
  title,
  label,
  value,
  onValueChange,
  onSubmit,
  originalValue,
  buttonClassName,
}: EditFactFieldDialogProps) => {
  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogTrigger asChild>
        <button
          className={cn(
            'group-data-[state=open]:animate-in group-data-[state=open]:fade-in p-0.5 text-gray-400 group-data-[state=closed]:hidden hover:text-gray-800',
            buttonClassName,
          )}
          onClick={(e) => e.stopPropagation()}
        >
          <Icon className="lucide--pencil" />
        </button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>

        <form
          onSubmit={(e) => {
            e.preventDefault();
            onSubmit(value);
            onOpenChange(false);
          }}
        >
          <Label>{label}</Label>
          <Textarea value={value} onChange={(e) => onValueChange(e.target.value)} className="min-h-32" />

          <div className="mt-4 flex justify-end gap-2">
            <Button onClick={() => onOpenChange(false)} type="button">
              Cancel
            </Button>
            <Button variant="primary" type="submit" disabled={value === originalValue}>
              Update {label}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export const DocViewerFullFact = ({
  factId,
  subsetId,
  showDocumentDetails = false,
  docId,
  showComments = true,
  gotoDocReference = () => {},
}: {
  factId: string;
  subsetId?: string;
  docId?: string;
  showDocumentDetails?: boolean;
  showComments?: boolean;
  gotoDocReference?: (docId: string, factId: string, pageNumber: number | undefined) => void;
}) => {
  const { matterId } = useParams();

  // Fetch full fact details when the panel opens
  const { data: fact, isLoading: isLoadingFact, isPreviousData } = useGetFactDetails(matterId, subsetId, factId);

  const [factDateText, setFactDateText] = useState('');
  const [isFactDateTextPopoverOpen, setIsFactDateTextPopoverOpen] = useState(false);
  const [factDescription, setFactDescription] = useState('');
  const [isFactDescriptionDialogOpen, setIsFactDescriptionDialogOpen] = useState(false);
  const [factSignificance, setFactSignificance] = useState('');
  const [isFactSignificanceDialogOpen, setIsFactSignificanceDialogOpen] = useState(false);
  const [relevance, setRelevance] = useState<Relevance | null>(null);

  const { mutate: updateFact, isLoading: isUpdatingFact } = useUpdateFact(matterId ?? '', subsetId ?? '');
  const queryClient = useQueryClient();

  const { data: subset } = useGetSubset(matterId, subsetId);

  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [isScrolledToBottom, setIsScrolledToBottom] = useState(false);

  useEffect(() => {
    const target = scrollContainerRef.current;
    if (target) {
      setIsScrolledToBottom(target.scrollHeight - target.scrollTop <= target.clientHeight + 1);
    }
  }, []);

  // Update local state when fact data loads
  useEffect(() => {
    if (fact) {
      setFactDateText(fact.factDateText ?? '');
      setFactDescription(fact.description ?? '');
      setFactSignificance(fact.significance ?? '');
      setRelevance(fact.relevance ?? null);
    }
  }, [fact]);

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const target = e.target as HTMLElement;
    setIsScrolledToBottom(target.scrollHeight - target.scrollTop <= target.clientHeight + 1);
  };

  const invalidateFactQueries = () => {
    if (subsetId) {
      queryClient.invalidateQueries([QueryEntity.SubsetFacts, { subsetId }]);
    }
    if (docId) {
      queryClient.invalidateQueries([QueryEntity.DocSubsetFacts, { docId, subsetId }]);
    }
  };

  const handleUpdateDateText = (newDateText: string) => {
    setFactDateText(newDateText);
    updateFact(
      { field: 'factDateText', value: newDateText, factId },
      {
        onSuccess: () => {
          invalidateFactQueries();
          trackCustomEvent(TrackingEventsV2.FactUpdateDate);
        },
      },
    );
  };

  const handleUpdateDescription = (newDescription: string) => {
    updateFact(
      { field: 'description', value: newDescription, factId },
      {
        onSuccess: () => {
          setFactDescription(newDescription);
          invalidateFactQueries();
          trackCustomEvent(TrackingEventsV2.FactUpdateContent);
        },
      },
    );
  };

  const handleUpdateSignificance = (newSignificance: string) => {
    updateFact(
      { field: 'significance', value: newSignificance, factId },
      {
        onSuccess: () => {
          setFactSignificance(newSignificance);
          invalidateFactQueries();
          trackCustomEvent(TrackingEventsV2.FactUpdateContent);
        },
      },
    );
  };

  const handleUpdateRelevance = (newRelevance: Relevance) => {
    setRelevance(newRelevance);
    updateFact(
      { field: 'relevance', value: newRelevance, factId },
      {
        onSuccess: () => {
          invalidateFactQueries();
          trackCustomEvent(TrackingEventsV2.FactUpdateRelevance);
        },
      },
    );
  };

  // Show loading state while fetching fact details
  if (isLoadingFact || !fact) {
    return (
      <div className="flex flex-col gap-4 p-4 text-xs">
        <div className="flex items-center gap-2">
          <Skeleton className="h-4 w-4" />
          <Skeleton className="h-4 w-32" />
        </div>
        <Skeleton className="h-16 w-full" />
        <Skeleton className="h-24 w-full" />
      </div>
    );
  }

  return (
    <div
      className={`max-h-96 overflow-auto mask-b-from-80% mask-b-to-99% text-xs transition-[mask-size] duration-200 ${isPreviousData ? 'opacity-50' : 'opacity-100'}`}
      style={{ maskSize: isScrolledToBottom ? '100% 120%' : '100% 100%' }}
      onScroll={handleScroll}
      ref={scrollContainerRef}
    >
      <div className="flex flex-col gap-2 px-1.5 pb-3">
        <div className="px-2 py-2">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex flex-wrap items-center gap-2 text-gray-800">
              <Icon className="lucide--calendar text-gray-500" />
              <span className="font-medium text-gray-900">
                {fact.factDateText ? capitalizeFirstLetter(fact.factDateText) : '(Date Unknown)'}
              </span>
              {fact.dateIsAmbiguous && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span>
                      <Icon className="lucide--triangle-alert text-amber-500" />
                    </span>
                  </TooltipTrigger>
                  <TooltipContent>
                    {fact.dateIsAmbiguousText || 'Date format is ambiguous, please check'}
                  </TooltipContent>
                </Tooltip>
              )}

              <Popover
                open={isFactDateTextPopoverOpen}
                onOpenChange={(isOpen) => {
                  setIsFactDateTextPopoverOpen(isOpen);
                  if (isOpen) {
                    setFactDateText(fact.factDateText ?? '');
                  }
                }}
              >
                <PopoverTrigger onClick={(e) => e.stopPropagation()}>
                  <Icon className="lucide--pencil text-xs text-gray-400 hover:text-gray-800" />
                </PopoverTrigger>
                <PopoverContent className="max-w-96" side="right">
                  <p className="text-gray-600">
                    Enter date in any format e.g. 2001, 9th June 2003, 2006-2007, September 2009
                  </p>
                  <form
                    onSubmit={(e) => {
                      e.preventDefault();
                      handleUpdateDateText(factDateText);
                      setIsFactDateTextPopoverOpen(false);
                    }}
                    className="mt-3 mb-1 flex gap-1.5"
                  >
                    <Input value={factDateText} onChange={(e) => setFactDateText(e.target.value)} />
                    <Button type="submit" disabled={factDateText === (fact.factDateText ?? '')}>
                      <Icon className="lucide--arrow-right" />
                    </Button>
                  </form>
                </PopoverContent>
              </Popover>
            </div>

            <div className="flex flex-wrap items-center gap-2">
              {fact.isKey && (
                <Chip className={cn(CHIP_COLOURS.key, 'border')}>
                  <Icon className="lucide--key-round" />
                  <span>Key</span>
                </Chip>
              )}

              {fact.isDisputed ? (
                <Chip className={cn(CHIP_COLOURS.disputed, 'mr-4 border')}>
                  <Icon className="lucide--triangle-alert" />
                  <span>Disputed</span>
                </Chip>
              ) : null}
            </div>
          </div>

          <div className="my-3 text-xs text-gray-900">
            <p className="flex items-start gap-2">
              <span className="grow text-gray-800">{factDescription || 'No description provided.'}</span>
              <EditFactFieldDialog
                isOpen={isFactDescriptionDialogOpen}
                onOpenChange={setIsFactDescriptionDialogOpen}
                title="Edit Fact Description"
                label="Fact Description"
                value={factDescription}
                onValueChange={setFactDescription}
                onSubmit={handleUpdateDescription}
                originalValue={fact.description ?? ''}
              />
            </p>
          </div>

          {showDocumentDetails && (
            <FactDocumentDetails fact={fact} gotoDocReference={gotoDocReference} fullDocRow={false} />
          )}
        </div>

        <div className="relative flex flex-col gap-4 rounded-lg border border-gray-200 bg-gray-50 px-4 pt-9 pb-4 text-xs">
          {subset?.name && (
            <div className="absolute -top-[1px] left-3 flex items-center gap-1.5 rounded-b-lg border bg-white px-2 py-1 text-xs font-medium text-gray-700">
              <Icon className="lucide--group" />
              <span>{subset.name}</span>
            </div>
          )}
          <div className="flex flex-col gap-2">
            <p className="flex items-center gap-2 font-medium text-gray-800">
              <span>Significance</span>
              <TooltipIcon
                side="right"
                tooltipContent={`Short analysis of this fact's significance as it pertains to the case issues.`}
              />
              <EditFactFieldDialog
                isOpen={isFactSignificanceDialogOpen}
                onOpenChange={setIsFactSignificanceDialogOpen}
                title="Edit Fact Significance"
                label="Fact Significance"
                value={factSignificance}
                onValueChange={setFactSignificance}
                onSubmit={handleUpdateSignificance}
                originalValue={fact.significance ?? ''}
                buttonClassName="text-[10px]"
              />
            </p>
            {factSignificance ? (
              <p className="text-gray-700">{factSignificance}</p>
            ) : isUpdatingFact ? (
              <div className="flex flex-col gap-1.5">
                <Skeleton className="h-4 w-9/10 bg-gray-200" />
                <Skeleton className="h-4 w-2/10 bg-gray-200" />
              </div>
            ) : (
              <p className="text-gray-500">No significance summary yet.</p>
            )}
          </div>

          <div className="flex flex-wrap gap-6">
            <div className="flex min-w-48 flex-col gap-1">
              <p className="flex items-center gap-2 font-medium text-gray-800">
                Relevant
                <TooltipIcon side="right" tooltipContent={`Is this fact relevant to the matter issues?`} />
              </p>
              {relevance ? (
                <div className="max-w-48">
                  <RelevanceSelector relevanceText={relevance} updateRelevance={handleUpdateRelevance} />
                </div>
              ) : (
                <ProcessingChip processingState={ProcessingState.Processing} processingText="Processing Relevance" />
              )}
            </div>
          </div>
        </div>

        <div className="mx-2 mt-2">{showComments && <CommentSection fact={fact} />}</div>
      </div>
    </div>
  );
};
